/**
 * A media URL as the server states it (`/api/public/solunary/media/<id>`, host-relative —
 * docs/hq-site-module/hq-site-api-contract.md §3.4) made loadable from the SPA, which may
 * be served from a different origin than the API (`REACT_APP_BACKEND_URL`). Absolute URLs
 * and static `/brand/...` assets pass through unchanged; anything that is not a string is
 * no image at all ("").
 *
 * Pure, no imports: the public landing page and the Super Admin editor both use it.
 */
export function platformMediaSrc(url) {
  if (typeof url !== "string" || url === "") return "";
  return url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${url}` : url;
}

/** True when the PATH of `v` (before any `?` / `#`) has a `.` or `..` segment, spelled
 *  literally or percent-encoded (`%2e`, any case). A browser resolves those before it
 *  requests, so `/api/../x` is `/x`: a path that only LOOKS like it is under `/api/`. */
const hasDotSegment = (v) => {
  const path = v.replace(/^https?:\/\/[^/?#]*/i, "").split(/[?#]/, 1)[0];
  return path.split("/").some((seg) => /^(\.|%2e){1,2}$/i.test(seg));
};

/**
 * `platformMediaSrc` for a URL that is data, not code: the address a browser may be handed
 * as an `<img src>` or an `<a href>`, or "" when the value is neither a platform path nor
 * a web URL. Only `/api/...` host-relative paths (made loadable from the SPA's origin) and
 * http(s) URLs pass; everything else, `javascript:`, `data:`, `//host`, a bare word, a
 * path outside `/api/`, is refused so the caller renders the text instead of a link. A
 * path with a dot segment is refused too: it is not the address it appears to be.
 */
export function safeMediaSrc(url) {
  if (typeof url !== "string") return "";
  const v = url.trim();
  const shaped = /^\/api\/[^\s\\]+$/.test(v) || /^https?:\/\/[^\s\\]+$/i.test(v);
  return shaped && !hasDotSegment(v) ? platformMediaSrc(v) : "";
}
